<template>
    <div class="mx-auto flex w-full max-w-190 flex-col gap-4">
        <header class="flex flex-col gap-1 border-b border-border pb-4">
            <h1 class="display-small-serif text-ink">{{ t("components.profileWizard.redesign.title") }}</h1>
            <p class="scaffold-body-small text-ink-muted">{{ t("components.profileWizard.redesign.subtitle") }}</p>
        </header>
        <div class="body-small pb-3 text-ink-muted">
            <p>
                {{ description }}{{ ` ${sharingDisclosure}` }}
                <!-- BaseButton for the shared focus and interaction styles; the utilities make it read as an inline link in this sentence. -->
                <BaseButton
                    theme="tertiary"
                    class="body-small inline px-0 py-0 align-baseline leading-[inherit] text-ink underline underline-offset-2 hover:bg-transparent hover:text-ink-muted"
                    @click="isArcadeOpen = true"
                    >{{ t("components.profileWizard.seeItInAction") }}</BaseButton
                >
            </p>
        </div>
        <div class="flex flex-col gap-3">
            <ProfileLandingComparison :viewer-name="viewerName" />
            <div class="flex flex-wrap items-center justify-end gap-2">
                <BaseButton v-if="isManualFallback" theme="tertiary" size="sm" @click="isSkipUploadDialogOpen = true">
                    {{ t("components.profileWizard.redesign.fillOutManually") }}
                </BaseButton>
                <BaseButton v-else-if="documentUploadEnabled" theme="neutral" @click="handleUpload">
                    {{ t("components.profileWizard.uploadInstead") }}
                </BaseButton>
                <BaseButton theme="primary" data-testid="profile-create-cta" @click="handlePrimaryClick">{{ primaryLabel }}</BaseButton>
            </div>
        </div>
        <UploadDocumentDialog
            v-model:open="isUploadDialogOpen"
            :document-upload-enabled="documentUploadEnabled"
            :free-text-enabled="isManualFallback"
            @create-profile="handleCreateProfile" />
        <ConfirmationDialog
            v-model:open="isSkipUploadDialogOpen"
            :title="t('components.profileWizard.manualDialog.title')"
            :text="t('components.profileWizard.manualDialog.text')"
            :confirm-label="t('components.profileWizard.manualDialog.confirm')"
            container-class="md:w-125"
            @confirm="handleSkip">
        </ConfirmationDialog>
        <DemoModal v-model:open="isArcadeOpen" :demo-url="COLLAB_PROFILE_DEMO_URL" :header-text="t('components.profileWizard.demoHeader')" />
    </div>
</template>

<script setup lang="ts">
import { router, usePage } from "@inertiajs/vue3";
import { type ShareBackTarget, withShareBackTarget } from "~vue/CollaborationProfile/composables/useProfileShareBack";
import { useProfileSharingCopy } from "~vue/CollaborationProfile/composables/useProfileSharingCopy";
import BaseButton from "~vue/components/BaseButton.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import DemoModal from "~vue/components/DemoModal.vue";
import ProfileLandingComparison from "~vue/components/ProfileWizard/ProfileLandingComparison.vue";
import UploadDocumentDialog from "~vue/components/ProfileWizard/UploadDocumentDialog.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { COLLAB_PROFILE_DEMO_URL, SAMPLE_SENDER_NAME } from "./profileLandingConstants";

const { t } = useI18n();

type ProfileCreationStartRedesignProps = {
    viewerName: string;
    hasPerspectiveReport: boolean;
    perspectiveAssessmentEnabled?: boolean;
    perspectiveReportEnabled?: boolean;
    documentUploadEnabled?: boolean;
    shareBackTarget?: ShareBackTarget | null;
};

const { sharingDisclosure } = useProfileSharingCopy();

const {
    viewerName,
    hasPerspectiveReport = false,
    perspectiveAssessmentEnabled = false,
    perspectiveReportEnabled = false,
    documentUploadEnabled = false,
    shareBackTarget = null,
} = defineProps<ProfileCreationStartRedesignProps>();

const emit = defineEmits<{
    createProfile: [fileIds?: number[], freeText?: string];
}>();

const page = usePage();

const perspectiveEnabled = computed(() => perspectiveAssessmentEnabled || perspectiveReportEnabled);
const isManualFallback = computed(() => !hasPerspectiveReport && !perspectiveEnabled.value);

const description = computed(() => {
    const name = { name: SAMPLE_SENDER_NAME.split(" ")[0] };
    if (hasPerspectiveReport) {
        return t("components.profileWizard.redesign.descriptionWithReport", name);
    }
    if (perspectiveEnabled.value) {
        return t("components.profileWizard.redesign.descriptionWithPerspective", name);
    }
    return documentUploadEnabled ? t("components.profileWizard.redesign.descriptionUploadOrDescribe", name) : t("components.profileWizard.redesign.descriptionDescribe", name);
});
const primaryLabel = computed(() =>
    !hasPerspectiveReport && perspectiveEnabled.value ? t("components.profileWizard.redesign.getStarted") : t("components.profileWizard.redesign.createMyProfile"),
);

const eventProps = computed(() => ({ has_perspective_report: hasPerspectiveReport }));

onMounted(() => {
    void logUserInteraction(AmplitudeEventNames.COLLABORATION_PROFILE_CREATION_VIEWED, eventProps.value);
});

const isUploadDialogOpen = ref(false);
const isSkipUploadDialogOpen = ref(false);
const isArcadeOpen = ref(false);

const handleUpload = () => {
    void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_UPLOAD_DOCUMENT_CLICKED, {
        ...eventProps.value,
        perspective_assessment_enabled: perspectiveAssessmentEnabled,
        perspective_report_enabled: perspectiveReportEnabled,
    });
    isUploadDialogOpen.value = true;
};

const handleCreateProfile = (fileIds?: number[], freeText?: string) => {
    emit("createProfile", fileIds, freeText);
};

const handleSkip = () => {
    void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_SKIP_DOCUMENT_UPLOAD, eventProps.value);
    emit("createProfile");
};

const handlePrimaryClick = () => {
    void logUserInteraction(AmplitudeEventNames.COLLABORATION_PROFILE_CREATION_CLICKED, eventProps.value);
    if (hasPerspectiveReport) {
        handleCreateProfile();
    } else if (perspectiveAssessmentEnabled) {
        router.visit(shareBackTarget ? withShareBackTarget("/perspective/start/?source=profile_share_back", shareBackTarget.id) : "/perspective");
    } else if (perspectiveReportEnabled) {
        window.open(`${page.props.align_host}/perspective/questions`, "_blank", "noopener,noreferrer");
    } else {
        handleUpload();
    }
};
</script>
